import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Paper from 'apollo-react/components/Paper';
import Typography from 'apollo-react/components/Typography';
import Grid from 'apollo-react/components/Grid';
import Box from 'apollo-react/components/Box';
import CircularProgress from 'apollo-react/components/CircularProgress';
import axiosInstance from '../../../axiosInstance';
import ViewOnlyFileDisplay from '../../common/ViewOnlyFileDisplay';
import { getCurrentUser } from '../../../services/authService';
import { hasRequestReviewPrivilege } from '../../../utils/adminPrivileges';

const formatDisplayDate = (d) => {
  if (!d) return '-';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? String(d) : x.toLocaleDateString('en-GB');
};

const HcpLicenseListDetail = ({ persona }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessAllowed, setAccessAllowed] = useState(persona !== 'admin');

  useEffect(() => {
    if (persona !== 'admin') {
      setAccessAllowed(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const me = await getCurrentUser();
      if (cancelled) return;
      const ok =
        me?.user?.is_super === 1 ||
        hasRequestReviewPrivilege(me?.allPrivileges || me?.privileges);
      setAccessAllowed(ok);
      if (!ok) {
        navigate('/admin/home', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [persona, navigate]);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/hcp-licenses-list/${id}`);
      setEntry(response.data);
    } catch (error) {
      console.error('Error fetching License Information details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!accessAllowed) return;
    loadDetail();
  }, [loadDetail, accessAllowed]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!entry) {
    return (
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        <Typography variant="body1">
          No HCP License details available.
        </Typography>
      </Paper>
    );
  }

  const additionalFiles = Array.isArray(entry.additionalAttachmentFiles)
    ? entry.additionalAttachmentFiles
    : [];

  return (
    <Box sx={{ padding: 3 }}>
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        <Box
          sx={{
            marginBottom: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h1" sx={{ fontWeight: 600 }}>
            HCP License
          </Typography>
        </Box>

        {entry.request && (
          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="title1"
              component="h2"
              sx={{
                fontWeight: 500,
                marginBottom: 3,
                color: '#333',
                paddingBottom: 1,
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              Request Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">
                  Reference Number
                </Typography>
                <Typography variant="body1">
                  {entry.request.reference_number || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">
                  Status
                </Typography>
                <Typography variant="body1">
                  {entry.requestProgressStatus || 'Active'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">
                  Applicant Name
                </Typography>
                <Typography variant="body1">
                  {entry.end_user?.username || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">
                  HCP Emirates ID (request)
                </Typography>
                <Typography variant="body1">
                  {entry.request.study_name || '-'}
                </Typography>
              </Grid>
              {entry.request.end_user && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">
                      Submitted By
                    </Typography>
                    <Typography variant="body1">
                      {entry.request.end_user.username || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {entry.request.end_user.email || '-'}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        )}

        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: '#333',
              paddingBottom: 1,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            HCP &amp; License
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                HCP Name
              </Typography>
              <Typography variant="body1">{entry.hcp_name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                HCP Emirates ID #
              </Typography>
              <Typography variant="body1">
                {entry.hcp_emirates_id || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                HCP Email
              </Typography>
              <Typography variant="body1">{entry.hcp_email || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                HCP Phone #
              </Typography>
              <Typography variant="body1">{entry.hcp_phone || '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                License #
              </Typography>
              <Typography variant="body1">
                {entry.license_number || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                License Issue Date
              </Typography>
              <Typography variant="body1">
                {formatDisplayDate(entry.license_issue_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                License Renewal/End Date
              </Typography>
              <Typography variant="body1">
                {formatDisplayDate(entry.license_end_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                Total CME Hours
              </Typography>
              <Typography variant="body1">
                {entry.total_cme_hours != null && entry.total_cme_hours !== ''
                  ? String(entry.total_cme_hours)
                  : '-'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: '#333',
              paddingBottom: 1,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            Documents
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ marginBottom: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}
                >
                  License Image
                </Typography>
                {entry.licenseImage ? (
                  <ViewOnlyFileDisplay
                    files={[
                      {
                        id: entry.licenseImage.id,
                        file_id: entry.licenseImage.id,
                        name: entry.licenseImage.original_name,
                        file_name: entry.licenseImage.original_name,
                        file_size: entry.licenseImage.file_size,
                        mime_type: entry.licenseImage.mime_type,
                      },
                    ]}
                    label=""
                    helperText="Download license image"
                    persona={persona}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: '#999', fontStyle: 'italic' }}
                  >
                    Not provided
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ marginBottom: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}
                >
                  Additional Attachments
                </Typography>
                {additionalFiles.length > 0 ? (
                  <ViewOnlyFileDisplay
                    files={additionalFiles.map((f) => ({
                      id: f.id,
                      file_id: f.id,
                      name: f.original_name,
                      file_name: f.original_name,
                      file_size: f.file_size,
                      mime_type: f.mime_type,
                    }))}
                    label=""
                    helperText="Download attachment"
                    persona={persona}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ color: '#999', fontStyle: 'italic' }}
                  >
                    None
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{ marginTop: 4, paddingTop: 3, borderTop: '1px solid #e0e0e0' }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                Created At
              </Typography>
              <Typography variant="body1">
                {new Date(entry.createdAt).toLocaleString('en-GB')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">
                Last Updated
              </Typography>
              <Typography variant="body1">
                {new Date(entry.updatedAt).toLocaleString('en-GB')}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default HcpLicenseListDetail;
